import { LitElement, html, css } from '~/lib/bn-lit-element';
import { LitTraceMixin } from '../utility/lit-trace-mixin';

const icons = {
  home: '/images/map_marker_binsentry_blue.png',
  high: '/images/map_marker_high.png',
  medium: '/images/map_marker_medium.png',
  low: '/images/map_marker_low.png',
  default: '/images/map_marker_binsentry_blue.png',
};

export class BnMapMarker extends LitTraceMixin(LitElement) {
  constructor() {
    super();
    this.latitude = 0.0;
    this.longitude = 0.0;
    this.icon = 'default';
  }

  static get properties() {
    return {
      draggable: { type: Boolean },
      icon: { type: String },
      latitude: { type: Number },
      longitude: { type: Number },
      map: { type: Object, attribute: false },
      marker: { type: Object, attribute: false },
      open: { type: Boolean },
      title: { type: String },
    };
  }

  set map(map) {
    this._map = map;
    // Marker will be rebuilt, so disconnect existing one from old map and listeners.
    if (this.marker) {
      this._info = null;
      this.marker.setMap(null);
      google.maps.event.clearInstanceListeners(this.marker);
    }
    if (this._map && this._map instanceof google.maps.Map) {
      this._mapReady();
    }
  }

  update(changedProperties) {
    if (changedProperties.has('latitude') || changedProperties.has('longitude')) {
      this.marker && this.marker.setPosition({
        lat: parseFloat(this.latitude || 0),
        lng: parseFloat(this.longitude || 0),
      });
      this.dispatchEvent(new CustomEvent('map-marker-move'));
    }
    if (changedProperties.has('draggable')) {
      this.marker && this.marker.setDraggable(this.draggable);
    }
    if (changedProperties.has('icon')) {
      this._iconChanged();
    }
    super.update(changedProperties);
  }

  _iconChanged() {
    if (this.marker) {
      this.marker.setIcon({
        url: icons[this.icon],
        scaledSize: new google.maps.Size(26, 36),
      });
    }
  }

  static get styles() {
    return css`
      :host {
        display: none;
      }
    `;
  }

  render() {
    return html`
      <div id="info">
        <slot @slotchange="${this._onSlotChange}"></slot>
      </div>
    `;
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.map = null;
  }

  _mapReady() {
    this.__trace('_mapReady');

    this.marker = new google.maps.Marker({
      map: this._map,
      position: {
        lat: parseFloat(this.latitude),
        lng: parseFloat(this.longitude),
      },
      draggable: this.draggable,
      visible: !this.hidden,
      title: this.title,
      icon: {
        url: icons[this.icon],
        scaledSize: new google.maps.Size(26, 36),
      },
    });

    this.marker.addListener('click', () => {
      this.dispatchEvent(new CustomEvent('click'));
    });

    this.marker.addListener('dragend', (e) => this._onDragEnd(e));
    this._updateInfo();
  }

  _onSlotChange() {
    this.shadowRoot
      .querySelector('slot')
      .assignedNodes()
      .forEach((n) => n.addEventListener('update', () => this._updateInfo()));
    this._updateInfo();
  }

  _onDragEnd(e) {
    this.latitude = e.latLng.lat();
    this.longitude = e.latLng.lng();
    this.dispatchEvent(new CustomEvent('map-marker-dragend'));
  }

  async _updateInfo() {
    if (!this._map) {
      this.__trace('no map yet');
      return;
    }
    const content = this.innerHTML.trim();
    if (content) {
      if (!this._info) {
        // Create a new infowindow
        this._info = new google.maps.InfoWindow();
        this._mouseoverHandle = this.marker.addListener('mouseover', () => {
          this._info.open(this._map, this.marker);
          this.dispatchEvent(new CustomEvent('map-marker-open'));
        });
        this._mouseoutHandle = this.marker.addListener('mouseout', () => {
          this._info.close();
          this.dispatchEvent(new CustomEvent('map-marker-close'));
        });
      }
      this._info.setContent(content);
    } else if (this._info) {
      // Destroy the existing infowindow.  It doesn't make sense to have an empty one.
      google.maps.event.removeListener(this._mouseoverHandle);
      google.maps.event.removeListener(this._mouseoutHandle);
      this._info = null;
    }
  }
}

window.customElements.define('bn-map-marker', BnMapMarker);
